import React from 'react'
import debounce from 'lodash.debounce'

// css
import './Search.scss'

// components
import {
  BUTTON_THEME,
  Icon,
  Input,
  INPUT_THEME,
  Section,
  BarLoader,
} from '@st-operations/uikit/src/v2/index.js'
import Header from '_components/header/index.jsx'
import MoreInfoDropdown from '_components/more-info-dropdown/index.jsx'
import SearchListView from '_components/SearchListView.jsx'
import LastEditedInsights from '_components/audience-insights/LastEditedInsights.jsx'

// libraries
import User from '_lib/User.js'
import getAudienceSize from '_lib/getAudienceSize.js'
import parseReports from '_lib/parseReports.js'
import sendAnalytics from '@st-operations/uikit/lib/sendAnalytics.js'
import {
  getCustomInsightsReport,
  getAudiencesSearch,
  getAllLastUpdatedReports,
  getUserLastUpdatedReports,
} from '_services/audience'
import { logErrorAsync } from '../lib/utility'

// constants
const TEAM_ID = require('../constants/sharethis_team_id.js')
const SEARCH_DEBOUNCE_DELAY = 500

// page
class SearchPage extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      audienceGroups: [],
      audienceObj: {
        branded: [],
        custom: [],
        whiteLabel: [],
      },
      currAudienceGroup: [],
      loading: false,
      pageSize: 10,
      size: 10,
      showInfo: false,
    }
  }

  componentDidMount() {
    this.mounted = true

    // retrieve last query from local storage
    let query = localStorage.getItem('search_query')

    User.watch(this, (user) => {
      if (user) {
        // choose which audience type is viewable
        this.selectAvailableAudience(user.acl[TEAM_ID])

        // get user's most recently updated reports
        this.getUserLastUpdatedReports(user)

        // get all last updated reports
        this.getAllLastUpdatedReports()

        // search for cached query
        if (query) {
          this.search(query)
        }
      }
    })
  }

  componentWillUnmount() {
    this.mounted = false
  }

  backToTop() {
    document.getElementById('content').scrollTo(0, 0)
  }

  changeSegments = (e, name) => {
    let { audienceObj, size } = this.state
    let audiences = audienceObj[name]
    if (window.innerWidth <= 800) {
      size = audiences.length
    }
    let audienceGroups = this.divideAudiences(audiences, size)
    this.setState({
      audienceGroups,
      currAudienceGroup: audienceGroups[0],
      page: 0,
      selected: name,
    })
  }

  divideAudiences(audiences, size) {
    if (audiences.length === 0) return [[]]
    let groups = []

    // divide audiences in groups of size 'size' from state
    for (let i = 0; i < Math.ceil(audiences.length / size); i++) {
      let group = audiences.slice(size * i, size + size * i)
      groups.push(group)
    }
    return groups
  }

  getAllLastUpdatedReports = () => {
    getAllLastUpdatedReports()
      .then((allReports) => {
        allReports.forEach(
          (report) => (report.name = report.audience_name || report.report_name || '-')
        )
        allReports = parseReports(allReports)
        if (this.mounted) {
          this.setState({ allReports })
        }
      })
      .catch((error) => {
        logErrorAsync(error)
        toast.error(error.message)
      })
  }

  getUserLastUpdatedReports = (user) => {
    getUserLastUpdatedReports({
      email: user.email,
    })
      .then((userReports) => {
        userReports.forEach((report) => (report.name = report.audience_name || report.report_name))
        userReports = parseReports(userReports)
        this.setState({ userReports })
      })
      .catch((error) => {
        logErrorAsync(error)
        toast.error(error.message)
      })
  }

  onChange = (query) => {
    this.setState({ currAudienceGroup: [], loading: true, query })
    this.search(query)
  }

  onPageChange = (page) => {
    this.setState({
      currAudienceGroup: page.value,
      page: page.label - 1,
    })
  }

  search = debounce((query) => {
    this.setState(
      {
        query: query || null,
        loading: true,
      },
      async () => {
        localStorage.setItem('search_query', query)

        if (query && query.length > 0) {
          try {
            const audiences = await getAudiencesSearch({
              kind: 'taxonomy',
              q: query,
              status: 'draft,approved,processed',
            })

            let { selected, size } = this.state

            // split audiences into branded and white-label segments
            let audienceObj = this.splitSegments(audiences)

            let customAudiences = await getAudiencesSearch({
              kind: 'custom',
              q: query,
              status: 'draft,approved,processed',
            })

            // search custom insights report
            const reports = await getCustomInsightsReport({
              query,
            })

            customAudiences = reports.concat(customAudiences)
            audienceObj['custom'] = customAudiences

            // combine and parse audiences
            audienceObj['all'] = parseReports(audiences.concat(customAudiences))

            // pick first audience group to show based on permissions
            let audienceGroups = this.divideAudiences(audienceObj[selected], size)

            // only update if query is the most recent one
            if (this.mounted && query === this.state.query) {
              this.setState({
                audienceGroups,
                audienceObj,
                currAudienceGroup: audienceGroups[0],
                loading: false,
                page: 0,
              })

              // event tracking
              sendAnalytics('search_insights', null, query)
            } else {
              this.setState({ loading: false })
            }
          } catch (err) {
            //err handling
            logErrorAsync(err)
          }
        }
      }
    )
  }, SEARCH_DEBOUNCE_DELAY)

  selectAvailableAudience = (permissions) => {
    let selected
    if (permissions && permissions['Audience Creator']) {
      selected = 'custom'
    } else if (permissions && permissions['Branded Audiences']) {
      selected = 'branded'
    } else if (permissions && permissions['White Label Audiences']) {
      selected = 'white_label'
    } else {
      selected = 'branded'
    }
    this.setState({ selected })
  }

  sortAudience = (kind, sortDir, recentReports) => {
    let { audienceObj, allReports, selected, size } = this.state

    // sort recent reports or audiences returned from search
    let collection
    recentReports ? (collection = allReports) : (collection = audienceObj[selected])

    if (kind === 'actual_size' || kind === 'audience_size') {
      collection.sort((a, b) => {
        let aSize = getAudienceSize(a) || a.audience_size
        let bSize = getAudienceSize(b) || b.audience_size

        if (aSize === null) {
          return sortDir === 1 ? -1 : 1
        }

        if (bSize === null) {
          return sortDir === 1 ? 1 : -1
        }

        return sortDir === 1 ? aSize - bSize : bSize - aSize
      })
    } else if (kind === 'name') {
      collection.sort((a, b) => {
        return sortDir === 1 ? (a.name > b.name ? 1 : -1) : a.name > b.name ? -1 : 1
      })
    } else if (kind === 'updated_at') {
      collection.sort((a, b) => {
        return sortDir === 1
          ? a.updated_at > b.updated_at
            ? 1
            : -1
          : a.updated_at > b.updated_at
          ? -1
          : 1
      })
    }
    if (recentReports) {
      this.setState({ allReports: collection })
    } else {
      let audienceGroups = this.divideAudiences(collection, size)
      this.setState({
        audienceGroups,
        currAudienceGroup: audienceGroups[0],
      })
    }
  }

  splitSegments = (audiences) => {
    let audienceObj = {}
    let branded = [],
      whiteLabel = []
    audiences.forEach((audience) => {
      if (
        (audience.taxonomy &&
          audience.taxonomy.slug &&
          audience.taxonomy.slug.startsWith('sharethis')) ||
        audience.is_branded
      ) {
        branded.push(audience)
      } else {
        whiteLabel.push(audience)
      }
    })
    audienceObj['branded'] = branded
    audienceObj['white_label'] = whiteLabel
    return audienceObj
  }

  handleCloseInfo = () => {
    this.setState((state) => ({ ...state, showInfo: false }))
  }

  render() {
    const {
      loading,
      page,
      pageSize,
      query,
      allReports,
      userReports,
      selected,
      audienceGroups,
      audienceObj,
      currAudienceGroup,
      showInfo,
    } = this.state

    return (
      <div id="search-page" data-query={query}>
        <Header
          title="Audience Insights"
          actions={[
            {
              theme: BUTTON_THEME.GHOST,
              children: (
                <Icon
                  type="outline"
                  icon="info"
                  color="#13682B"
                  className="header__button"
                  width="24px"
                  height="24px"
                />
              ),
              onClick: () =>
                this.setState((state) => {
                  return { ...state, showInfo: !state.showInfo }
                }),
            },
          ]}
        />
        <If condition={showInfo}>
          <MoreInfoDropdown onClose={this.handleCloseInfo} />
        </If>

        <div className="inner">
          <section className="search__header">
            <h1 className="search__title">Explore 30,000+ Insights</h1>
            <h2 className="search__subtitle">
              Start building, exploring and activating audiences today
            </h2>
            <Input
              className="search__input-container"
              inputStyle={{ padding: '27px 48px', borderRadius: 2 }}
              onChange={this.onChange}
              placeholder={'Search for insight reports eg: lawyers'}
              theme={INPUT_THEME.SEARCH}
              value={query || ''}
              iconStyle={{ left: 24, width: 16 }}
            />
          </section>

          <If condition={query}>
            <Section className="center">
              <If condition={loading}>
                <BarLoader />
              </If>
              <If condition={!loading}>
                <SearchListView
                  audienceGroups={audienceGroups}
                  audienceObj={audienceObj}
                  changeSegments={this.changeSegments}
                  currAudienceGroup={currAudienceGroup}
                  history={this.props.history}
                  onPageChange={this.onPageChange}
                  page={page}
                  query={query}
                  selected={selected}
                  sortAudience={this.sortAudience}
                />
              </If>
            </Section>
          </If>

          <If condition={!query}>
            <Section>
              <LastEditedInsights
                all_reports={allReports}
                page_size={pageSize}
                sortAudience={this.sortAudience}
                user_reports={userReports}
              />
            </Section>
          </If>
        </div>
      </div>
    )
  }
}

// export
export default SearchPage
